import { createContext, useContext, useMemo, useState, ReactNode, useCallback, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getPageTips,
  getPageSection,
  getSectionTips,
  DEFAULT_SPOTTER_SUGGESTIONS,
  PageTipConfig,
  SectionTipConfig,
} from "@/config/page-tips";

export interface PageContextType {
  // Current page info
  path: string;
  pageTitle: string | undefined;
  section: string;
  params: Record<string, string | undefined>;

  // Tips content
  tips: PageTipConfig | undefined;
  hasTips: boolean;
  spotterSuggestions: string[];

  // Section tips
  sectionTips: SectionTipConfig | undefined;
  activeSectionTips: SectionTipConfig | undefined;

  // Tips panel state
  isTipsPanelOpen: boolean;
  openTipsPanel: () => void;
  closeTipsPanel: () => void;
  toggleTipsPanel: () => void;
  openSectionTips: (sectionId: string) => void;
}

const PageContext = createContext<PageContextType | undefined>(undefined);

export function PageContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const params = useParams();
  const [isTipsPanelOpen, setIsTipsPanelOpen] = useState(false);
  const [activeSectionTips, setActiveSectionTips] = useState<SectionTipConfig | undefined>(undefined);

  const tips = useMemo(() => {
    return getPageTips(location.pathname);
  }, [location.pathname]);

  const section = useMemo(() => {
    return getPageSection(location.pathname);
  }, [location.pathname]);

  // Get section tips for the current section
  const sectionTips = useMemo(() => {
    return getSectionTips(section);
  }, [section]);

  const spotterSuggestions = useMemo(() => {
    // If section tips panel is open, show section suggestions
    if (activeSectionTips) {
      return activeSectionTips.spotterSuggestions;
    }
    return tips?.spotterSuggestions ?? DEFAULT_SPOTTER_SUGGESTIONS;
  }, [tips, activeSectionTips]);

  const openTipsPanel = useCallback(() => {
    setActiveSectionTips(undefined); // Clear section tips when opening page tips
    setIsTipsPanelOpen(true);
  }, []);

  const closeTipsPanel = useCallback(() => {
    setIsTipsPanelOpen(false);
    setActiveSectionTips(undefined);
  }, []);

  const toggleTipsPanel = useCallback(() => {
    setIsTipsPanelOpen(prev => {
      if (prev) setActiveSectionTips(undefined);
      return !prev;
    });
  }, []);

  const openSectionTips = useCallback((sectionId: string) => {
    const sectionConfig = getSectionTips(sectionId);
    if (sectionConfig) {
      setActiveSectionTips(sectionConfig);
      setIsTipsPanelOpen(true);
    }
  }, []);

  // Keyboard shortcut: ? key to toggle tips panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger on ? key, not when typing in inputs
      if (e.key === "?" &&
          !["INPUT", "TEXTAREA", "SELECT"].includes((e.target as HTMLElement).tagName) &&
          !(e.target as HTMLElement).isContentEditable) {
        e.preventDefault();
        setIsTipsPanelOpen(prev => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const value = useMemo<PageContextType>(() => ({
    path: location.pathname,
    pageTitle: tips?.title,
    section,
    params: params as Record<string, string | undefined>,
    tips,
    hasTips: !!tips,
    spotterSuggestions,
    sectionTips,
    activeSectionTips,
    isTipsPanelOpen,
    openTipsPanel,
    closeTipsPanel,
    toggleTipsPanel,
    openSectionTips,
  }), [
    location.pathname,
    tips,
    section,
    params,
    spotterSuggestions,
    sectionTips,
    activeSectionTips,
    isTipsPanelOpen,
    openTipsPanel,
    closeTipsPanel,
    toggleTipsPanel,
    openSectionTips,
  ]);

  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
}

export function usePageContext() {
  const context = useContext(PageContext);
  if (context === undefined) {
    throw new Error("usePageContext must be used within a PageContextProvider");
  }
  return context;
}
